import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Clock, Sparkles, XCircle } from "lucide-react";

export type BannerVariant =
  | "comfortable"
  | "warning"
  | "urgent"
  | "expiring"
  | "expired"
  | "limit-notice"
  | "base-upsell"
  | "canceling";

export interface BannerConfig {
  variant: BannerVariant;
  Icon: LucideIcon;
  heading: string;
  body: string;
  cta: string;
  ctaTo?: string;
  dismissible?: boolean;
}

function daysUntil(isoDate: string): number {
  const now = new Date();
  const target = new Date(isoDate);
  const diffMs = target.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  });
}

export function resolveBannerConfig(params: {
  isTrial: boolean;
  hasActivePaidSubscription: boolean;
  trialExpiresAt: string | null;
  bypassActive: boolean;
  planId: string;
  limitNotice: string | null;
  baseUpsellDismissed: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}): BannerConfig | null {
  const {
    isTrial,
    hasActivePaidSubscription,
    trialExpiresAt,
    bypassActive,
    planId,
    limitNotice,
    baseUpsellDismissed,
    cancelAtPeriodEnd,
    currentPeriodEnd,
  } = params;

  if (bypassActive) {
    return null;
  }

  if (hasActivePaidSubscription && cancelAtPeriodEnd) {
    return {
      variant: "canceling",
      Icon: AlertTriangle,
      heading: "Subscription ending",
      body: currentPeriodEnd
        ? `Your plan ends on ${formatDate(currentPeriodEnd)}. Renew to keep access.`
        : "Your plan is set to cancel. Renew to keep access.",
      cta: "Renew subscription",
    };
  }

  if (!hasActivePaidSubscription) {
    if (isTrial && trialExpiresAt) {
      const days = daysUntil(trialExpiresAt);

      if (days > 7) {
        return {
          variant: "comfortable",
          Icon: Sparkles,
          heading: `${days} days left in your trial`,
          body: "You're on a full Core trial. Subscribe to keep all features active.",
          cta: "View plans",
        };
      }
      if (days >= 4) {
        return {
          variant: "warning",
          Icon: Clock,
          heading: `${days} days left`,
          body: "Subscribe before your trial ends to avoid losing access to reports and exports.",
          cta: "Subscribe now",
        };
      }
      if (days >= 1) {
        return {
          variant: "urgent",
          Icon: AlertTriangle,
          heading: days === 1 ? "1 day left" : `${days} days left`,
          body: "Your trial expires very soon. Subscribe now to avoid any disruption.",
          cta: "Subscribe now",
        };
      }
      return {
        variant: "expiring",
        Icon: XCircle,
        heading: "Trial ends today",
        body: "Subscribe now to keep your reports, exports, and Core features.",
        cta: "Subscribe now",
      };
    }

    if (limitNotice) {
      return {
        variant: "limit-notice",
        Icon: AlertTriangle,
        heading: "Site limit reached",
        body: limitNotice,
        cta: "Upgrade",
      };
    }

    return {
      variant: "expired",
      Icon: XCircle,
      heading: "Trial ended",
      body: "You're on the free plan. Upgrade to restore reports, exports, and full access.",
      cta: "Upgrade now",
    };
  }

  if (planId === "base" && !baseUpsellDismissed) {
    return {
      variant: "base-upsell",
      Icon: Sparkles,
      heading: "Unlock more on Core",
      body: "PDF exports, automated daily reports, and voice notes are available on Core.",
      cta: "See Core plan",
      ctaTo: "/billing#core-plan",
      dismissible: true,
    };
  }

  return null;
}
